import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ClipboardListIcon,
  ClockIcon,
  TrendingUpIcon,
  BellIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  UsersIcon,
  CheckCircle2Icon,
  InboxIcon
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  useGetWorkspaceKpi,
  getGetWorkspaceReportsQueryOptions,
  useGetCivicCategories
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { GetWorkspaceReportsStatus, GetWorkspaceReportsSeverity, GetWorkspaceReportsSort } from '@cdf/api-client-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { getStatusConfig, formatRelativeTime, SEVERITY_LABELS } from '@/lib/report-helpers';

function KpiCard({ icon: Icon, label, value, sublabel }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  sublabel?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-5" />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold leading-none">{value}</p>
          <p className="mt-1 text-sm text-muted-foreground">{label}</p>
          {sublabel && <p className="mt-0.5 text-xs text-muted-foreground/70">{sublabel}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return '< 1 min';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} hr`;
  return `${Math.round(ms / 86_400_000)} days`;
}

export function WorkspacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const statusFilter = searchParams.get('status') ?? 'all';
  const severityFilter = searchParams.get('severity') ?? 'all';
  const sortFilter = searchParams.get('sort') ?? 'newest';

  const { data: kpi, isLoading: kpiLoading } = useGetWorkspaceKpi();
  const { data: categories } = useGetCivicCategories();

  const queryParams = React.useMemo(() => ({
    status: statusFilter !== 'all' ? (statusFilter as GetWorkspaceReportsStatus) : undefined,
    severity: severityFilter !== 'all' ? (severityFilter as GetWorkspaceReportsSeverity) : undefined,
    sort: sortFilter as GetWorkspaceReportsSort,
    limit: 100
  }), [statusFilter, severityFilter, sortFilter]);

  const { data: reportData, isLoading: reportsLoading } = useQuery(
    getGetWorkspaceReportsQueryOptions(queryParams)
  );

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  }

  const reports = reportData?.reports ?? [];
  const categoryMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of categories?.categories ?? []) {
      if (c.id) map[c.id] = c.name ?? 'Unknown';
    }
    return map;
  }, [categories]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="Staff Workspace"
        icon={ClipboardListIcon}
        description="Triage, assign, and manage reports for your organisation."
      />

      {/* KPI Dashboard */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
        {kpiLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))
        ) : (
          <>
            <KpiCard
              icon={InboxIcon}
              label="Open Reports"
              value={kpi?.openCount ?? 0}
              sublabel={`of ${kpi?.totalReports ?? 0} total`}
            />
            <KpiCard
              icon={CheckCircle2Icon}
              label="Closed Reports"
              value={kpi?.closedCount ?? 0}
            />
            <KpiCard
              icon={ClockIcon}
              label="Median Resolve"
              value={kpi?.medianTimeToResolveMs ? formatDuration(kpi.medianTimeToResolveMs) : '—'}
              sublabel="time to close"
            />
            <KpiCard
              icon={BellIcon}
              label="Nudges"
              value={kpi?.nudgeCount ?? 0}
              sublabel="from tracking page"
            />
            <KpiCard
              icon={TrendingUpIcon}
              label="Categories"
              value={kpi?.byCategory?.length ?? 0}
              sublabel="with reports"
            />
          </>
        )}
      </div>

      {/* Category breakdown */}
      {kpi?.byCategory && kpi.byCategory.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Reports by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {kpi.byCategory.map((cat, i) => (
                <Badge key={i} variant="secondary" className="gap-1.5">
                  {cat.name ?? 'Unknown'}
                  <span className="text-muted-foreground">{cat.count ?? 0}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="duplicate">Duplicate</SelectItem>
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={(v) => updateFilter('severity', v)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortFilter} onValueChange={(v) => updateFilter('sort', v)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="upvotes">Most Upvoted</SelectItem>
            <SelectItem value="severity">Highest Severity</SelectItem>
          </SelectContent>
        </Select>

        <span className="ml-auto text-sm text-muted-foreground">
          {reports.length} report{reports.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Triage queue table */}
      <Card>
        <CardContent className="p-0">
          {reportsLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <UsersIcon className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No reports match these filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Status</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="hidden md:table-cell">Description</TableHead>
                  <TableHead className="w-[80px]">Severity</TableHead>
                  <TableHead className="w-[60px] text-center">↑</TableHead>
                  <TableHead className="hidden lg:table-cell">Assigned</TableHead>
                  <TableHead className="w-[100px]">Created</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => {
                  const statusCfg = getStatusConfig(report.status ?? 'submitted');
                  return (
                    <TableRow key={report.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/workspace/reports/${report.id}`)}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {report.isPriority && (
                              <AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500" />
                            )}
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.badgeClass}`}>
                              <span className={`size-1.5 rounded-full ${statusCfg.dotClass}`} />
                              {statusCfg.label}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{report.customTypeLabel ? `Other — ${report.customTypeLabel}` : (report.categoryName ?? categoryMap[report.categoryId ?? ''] ?? 'Unknown')}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground hidden md:table-cell">
                          {report.description ?? '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {SEVERITY_LABELS[report.severity as keyof typeof SEVERITY_LABELS] ?? report.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-sm tabular-nums">{report.upvoteCount ?? 0}</TableCell>
                        <TableCell className="text-sm hidden lg:table-cell">
                          {report.assignedTo?.staffName ?? <span className="text-muted-foreground/60">Unassigned</span>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {report.createdAt ? formatRelativeTime(report.createdAt) : '—'}
                        </TableCell>
                        <TableCell>
                          <ArrowRightIcon className="size-4 text-muted-foreground/40" />
                        </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
